import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Utensils, Sparkles, TrendingUp, Flame, Calculator, Loader2, Plus, X } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const DietTracker = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [calculating, setCalculating] = useState(false);
  const [foodItems, setFoodItems] = useState([{ name: '', quantity: '', unit: 'grams' }]);
  const [formData, setFormData] = useState({
    meal_type: 'breakfast',
    food_items: '',
    calories: 0,
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      const response = await axios.get(`${API}/diet`);
      setEntries(response.data);
    } catch (error) {
      console.error('Error loading diet entries:', error);
    }
  };

  const addFoodItem = () => {
    setFoodItems([...foodItems, { name: '', quantity: '', unit: 'grams' }]);
  };

  const removeFoodItem = (index) => {
    const newItems = foodItems.filter((_, i) => i !== index);
    setFoodItems(newItems.length > 0 ? newItems : [{ name: '', quantity: '', unit: 'grams' }]);
  };

  const updateFoodItem = (index, field, value) => {
    const newItems = [...foodItems];
    newItems[index][field] = value;
    setFoodItems(newItems);
  };

  const calculateCalories = async () => {
    // Check if all items have name and quantity
    const validItems = foodItems.filter(item => item.name && item.quantity);
    if (validItems.length === 0) {
      toast.error('Please add at least one food item with quantity');
      return;
    }

    setCalculating(true);
    try {
      // Create food description for AI
      const foodDescription = validItems.map(item => 
        `${item.name} (${item.quantity} ${item.unit})`
      ).join(', ');

      // Call AI to calculate calories
      const response = await axios.post(`${API}/diet/calculate-calories`, {
        food_items: foodDescription
      });

      const totalCalories = response.data.total_calories;
      const breakdown = response.data.breakdown;

      // Update form data
      setFormData({
        ...formData,
        food_items: foodDescription,
        calories: totalCalories,
        notes: breakdown || formData.notes
      });

      toast.success(`Calculated: ${totalCalories} calories`);
    } catch (error) {
      toast.error('Failed to calculate calories. Please try again.');
      console.error('Calorie calculation error:', error);
    } finally {
      setCalculating(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.food_items) {
      toast.error('Please add food items');
      return;
    }

    if (!formData.calories) {
      toast.error('Please calculate calories first');
      return;
    }

    try {
      await axios.post(`${API}/diet`, formData);
      toast.success('Diet entry added!');
      loadEntries();
      
      // Reset form
      setFormData({
        meal_type: 'breakfast',
        food_items: '',
        calories: 0,
        notes: '',
        date: new Date().toISOString().split('T')[0]
      });
      setFoodItems([{ name: '', quantity: '', unit: 'grams' }]);
    } catch (error) {
      toast.error('Failed to add entry');
    }
  };

  // Calculate daily totals
  const getDailyTotals = (date) => {
    const dayEntries = entries.filter(e => e.date === date);
    const totalCalories = dayEntries.reduce((sum, e) => sum + (e.calories || 0), 0);
    const mealCounts = {
      breakfast: dayEntries.filter(e => e.meal_type === 'breakfast').length,
      lunch: dayEntries.filter(e => e.meal_type === 'lunch').length,
      dinner: dayEntries.filter(e => e.meal_type === 'dinner').length,
      snack: dayEntries.filter(e => e.meal_type === 'snack').length
    };
    return { totalCalories, mealCounts };
  };

  // Get unique dates
  const uniqueDates = [...new Set(entries.map(e => e.date))].sort().reverse();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-8 sm:py-8 pt-24 " data-testid="diet-tracker-page">
        <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('dietTracker.title')}</h1>
        <p className="text-gray-600 text-lg mb-8">{t('dietTracker.subtitle')}</p>

        {/* Daily Statistics */}
        {entries.length > 0 && uniqueDates.length > 0 && (
          <div className="grid md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-orange-500 to-red-600 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Flame className="w-10 h-10" />
                <span className="text-4xl font-bold">{getDailyTotals(uniqueDates[0]).totalCalories}</span>
              </div>
              <p className="text-sm opacity-90">{t('dietTracker.todaysCalories')}</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-teal-500 to-cyan-600 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Utensils className="w-10 h-10" />
                <span className="text-4xl font-bold">
                  {Object.values(getDailyTotals(uniqueDates[0]).mealCounts).reduce((a, b) => a + b, 0)}
                </span>
              </div>
              <p className="text-sm opacity-90">{t('dietTracker.mealsToday')}</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-cyan-500 to-teal-600 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-10 h-10" />
                <span className="text-4xl font-bold">{entries.length}</span>
              </div>
              <p className="text-sm opacity-90">{t('dietTracker.totalEntries')}</p>
            </Card>

            <Card className="p-6 bg-gradient-to-br from-teal-600 to-cyan-700 text-white shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <Calculator className="w-10 h-10" />
                <span className="text-4xl font-bold">
                  {Math.round(entries.reduce((sum, e) => sum + (e.calories || 0), 0) / entries.length)}
                </span>
              </div>
              <p className="text-sm opacity-90">{t('dietTracker.avgCalories')}</p>
            </Card>
          </div>
        )}

        <Card className="p-8 bg-white shadow-xl mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-teal-600" />
            AI-Powered Calorie Calculator
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <Label>Meal Type</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.meal_type}
                onChange={(e) => setFormData({ ...formData, meal_type: e.target.value })}
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center">
              <Label className="text-lg font-semibold">Food Items & Quantities</Label>
              <Button
                type="button"
                onClick={addFoodItem}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </div>

            {foodItems.map((item, index) => (
              <div key={`item-${index}`} className="grid grid-cols-12 gap-3 items-end">
                <div className="col-span-5">
                  <Label className="text-sm">Food Item</Label>
                  <Input
                    value={item.name}
                    onChange={(e) => updateFoodItem(index, 'name', e.target.value)}
                    placeholder="e.g., Rice, Chicken, Apple"
                    className="mt-1"
                  />
                </div>
                <div className="col-span-3">
                  <Label className="text-sm">Quantity</Label>
                  <Input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateFoodItem(index, 'quantity', e.target.value)}
                    placeholder="100"
                    className="mt-1"
                  />
                </div>
                <div className="col-span-3">
                  <Label className="text-sm">Unit</Label>
                  <select
                    value={item.unit}
                    onChange={(e) => updateFoodItem(index, 'unit', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md mt-1"
                  >
                    <option value="grams">grams</option>
                    <option value="ml">ml</option>
                    <option value="cup">cup</option>
                    <option value="piece">piece</option>
                    <option value="tbsp">tbsp</option>
                    <option value="tsp">tsp</option>
                  </select>
                </div>
                {foodItems.length > 1 && (
                  <div className="col-span-1">
                    <Button
                      type="button"
                      onClick={() => removeFoodItem(index)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>

          <Button
            onClick={calculateCalories}
            disabled={calculating}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 mb-4"
            data-testid="calculate-calories-btn"
          >
            {calculating ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="w-5 h-5 mr-2" />
                Calculate Calories with AI
              </>
            )}
          </Button>

          {formData.calories > 0 && (
            <div className="bg-gradient-to-r from-green-50 to-teal-50 p-6 rounded-lg border-2 border-green-200 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-800">Calculated Calories</h3>
                <div className="flex items-center gap-2">
                  <Flame className="w-6 h-6 text-orange-600" />
                  <span className="text-3xl font-bold text-gray-800">{formData.calories}</span>
                  <span className="text-lg text-gray-600">cal</span>
                </div>
              </div>
              {formData.notes && (
                <div className="text-sm text-gray-700 whitespace-pre-wrap bg-white p-4 rounded-lg">
                  <strong>Breakdown:</strong>
                  <br />
                  {formData.notes}
                </div>
              )}
            </div>
          )}

          <div className="mb-6">
            <Label>Additional Notes (optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="How did you feel after eating?"
              className="mt-1"
            />
          </div>

          <Button
            onClick={handleAdd}
            disabled={!formData.calories}
            className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white py-4 text-lg font-semibold hover:shadow-xl transition-all"
            data-testid="add-diet-entry-btn"
          >
            <Utensils className="w-5 h-5 mr-2" />
            Save Diet Entry
          </Button>
        </Card>

        <Card className="p-6 bg-white shadow-lg">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Your Diet History</h2>
          
          {uniqueDates.length === 0 ? (
            <div className="text-center py-12">
              <Utensils className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No diet entries yet</p>
              <p className="text-gray-400 text-sm mt-2">Start tracking your meals to see insights</p>
            </div>
          ) : (
            <div className="space-y-8">
              {uniqueDates.map((date) => {
                const dayEntries = entries.filter(e => e.date === date);
                const dayTotals = getDailyTotals(date);
                
                return (
                  <div key={date} className="border-2 border-gray-200 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-4 pb-4 border-b-2 border-gray-200">
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">
                          {new Date(date).toLocaleDateString('en-US', { 
                            weekday: 'long', 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {dayEntries.length} meal{dayEntries.length !== 1 ? 's' : ''} logged
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Flame className="w-6 h-6 text-orange-600" />
                          <span className="text-3xl font-bold text-gray-800">{dayTotals.totalCalories}</span>
                          <span className="text-lg text-gray-600">cal</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Total for the day</p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {dayEntries.map((entry) => (
                        <div key={entry.id} className="p-4 bg-gradient-to-r from-gray-50 to-teal-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-3">
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-teal-100 text-teal-700 capitalize">
                                <Utensils className="w-4 h-4 mr-1" />
                                {entry.meal_type}
                              </span>
                              <span className="text-xs text-gray-500">
                                {new Date(entry.timestamp).toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </span>
                            </div>
                            {entry.calories && (
                              <div className="flex items-center gap-1">
                                <Flame className="w-4 h-4 text-orange-500" />
                                <span className="text-lg font-bold text-gray-800">{entry.calories}</span>
                                <span className="text-sm text-gray-600">cal</span>
                              </div>
                            )}
                          </div>
                          <p className="text-gray-700 font-medium mb-1">{entry.food_items}</p>
                          {entry.notes && (
                            <p className="text-sm text-gray-600 italic mt-2 bg-white p-2 rounded">
                              {entry.notes}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Daily meal breakdown */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="grid grid-cols-4 gap-2">
                        {Object.entries(dayTotals.mealCounts).map(([meal, count]) => (
                          count > 0 && (
                            <div key={meal} className="text-center p-2 bg-gray-50 rounded">
                              <p className="text-xs text-gray-500 capitalize">{meal}</p>
                              <p className="text-lg font-bold text-gray-800">{count}</p>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DietTracker;
